import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faHandHoldingUsd, faFileInvoiceDollar, faIdCard, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import './Fees.css';

const Fees = () => {
  return (
    <div className="fees-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <h1>Fee Information</h1>
          <p>Transparent pricing for our healthcare services</p>
        </div>
      </div>

      {/* Fee Introduction */}
      <section className="section fee-intro">
        <div className="container">
          <div className="intro-content">
            <h2>Our Billing Policy</h2>
            <p>At Tea Gardens GP, we strive to provide high-quality healthcare services at reasonable rates. We believe in transparent pricing and ensuring our patients understand all costs associated with their care. Payment is required at the time of consultation, and we offer various payment methods for your convenience.</p>
            <p>Medicare rebates are available for eligible services and can be processed on the spot if you have registered your bank details with Medicare. For patients without Medicare, or for services not covered by Medicare, full payment is required.</p>
            <p>Please note that our fees are subject to periodic review. The information provided below is current as of July 2025.</p>
          </div>
        </div>
      </section>

      {/* Standard Consultation Fees */}
      <section className="section standard-fees">
        <div className="container">
          <div className="section-title">
            <h2>Standard Consultation Fees</h2>
            <p>Fees for our most common consultation types</p>
          </div>

          <div className="fee-table-container">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Duration</th>
                  <th>Fee</th>
                  <th>Medicare Rebate</th>
                  <th>Gap Payment</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Standard Consultation</td>
                  <td>0-15 minutes</td>
                  <td>$85.00</td>
                  <td>$39.75</td>
                  <td>$45.25</td>
                </tr>
                <tr>
                  <td>Long Consultation</td>
                  <td>16-30 minutes</td>
                  <td>$125.00</td>
                  <td>$76.95</td>
                  <td>$48.05</td>
                </tr>
                <tr>
                  <td>Extended Consultation</td>
                  <td>31-45 minutes</td>
                  <td>$165.00</td>
                  <td>$113.30</td>
                  <td>$51.70</td>
                </tr>
                <tr>
                  <td>Comprehensive Consultation</td>
                  <td>46+ minutes</td>
                  <td>$205.00</td>
                  <td>$148.20</td>
                  <td>$56.80</td>
                </tr>
                <tr>
                  <td>Telehealth Consultation</td>
                  <td>0-15 minutes</td>
                  <td>$75.00</td>
                  <td>$39.75</td>
                  <td>$35.25</td>
                </tr>
                <tr>
                  <td>After Hours Consultation</td>
                  <td>0-15 minutes</td>
                  <td>$110.00</td>
                  <td>$51.00</td>
                  <td>$59.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Bulk Billing Information */}
      <section className="section bulk-billing">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="bulk-billing-content">
                <div className="section-title text-left">
                  <h2>Bulk Billing</h2>
                </div>
                <p>We offer bulk billing (no out-of-pocket expense) for eligible patients in the following categories:</p>
                <ul className="bulk-billing-list">
                  <li>Children under 16 years of age</li>
                  <li>Pensioners with valid Pensioner Concession Cards</li>
                  <li>Healthcare Card holders</li>
                  <li>Department of Veterans' Affairs (DVA) card holders</li>
                  <li>Patients with chronic conditions under a GP Management Plan</li>
                </ul>
                <p>Please note that some services may not be eligible for bulk billing even for the above patient categories. Our reception staff will advise you of any potential costs before your appointment.</p>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="bulk-billing-image">
                <img src="https://via.placeholder.com/600x400" alt="Medicare Card" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Procedure Fees */}
      <section className="section procedure-fees">
        <div className="container">
          <div className="section-title">
            <h2>Common Procedure Fees</h2>
            <p>Fees for procedures performed at our practice</p>
          </div>

          <div className="fee-table-container">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Procedure</th>
                  <th>Fee</th>
                  <th>Medicare Rebate</th>
                  <th>Gap Payment</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Skin Excision - Small</td>
                  <td>$150.00</td>
                  <td>$72.80</td>
                  <td>$77.20</td>
                </tr>
                <tr>
                  <td>Skin Excision - Medium</td>
                  <td>$220.00</td>
                  <td>$110.35</td>
                  <td>$109.65</td>
                </tr>
                <tr>
                  <td>Skin Excision - Large</td>
                  <td>$280.00</td>
                  <td>$168.30</td>
                  <td>$111.70</td>
                </tr>
                <tr>
                  <td>Ingrown Toenail Treatment</td>
                  <td>$180.00</td>
                  <td>$85.60</td>
                  <td>$94.40</td>
                </tr>
                <tr>
                  <td>Implanon Insertion</td>
                  <td>$150.00</td>
                  <td>$85.55</td>
                  <td>$64.45</td>
                </tr>
                <tr>
                  <td>Implanon Removal</td>
                  <td>$150.00</td>
                  <td>$85.55</td>
                  <td>$64.45</td>
                </tr>
                <tr>
                  <td>IUD Insertion</td>
                  <td>$250.00</td>
                  <td>$125.65</td>
                  <td>$124.35</td>
                </tr>
                <tr>
                  <td>IUD Removal</td>
                  <td>$150.00</td>
                  <td>$85.55</td>
                  <td>$64.45</td>
                </tr>
                <tr>
                  <td>Wound Dressing - Small</td>
                  <td>$45.00</td>
                  <td>$27.45</td>
                  <td>$17.55</td>
                </tr>
                <tr>
                  <td>Wound Dressing - Large</td>
                  <td>$75.00</td>
                  <td>$39.75</td>
                  <td>$35.25</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="fee-note">
            <p>* Additional costs may apply for consumables and pathology services.</p>
          </div>
        </div>
      </section>

      {/* Health Assessments */}
      <section className="section health-assessments">
        <div className="container">
          <div className="section-title">
            <h2>Health Assessments & Special Services</h2>
            <p>Fees for comprehensive health assessments and specialized services</p>
          </div>

          <div className="fee-table-container">
            <table className="fee-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Fee</th>
                  <th>Medicare Rebate</th>
                  <th>Gap Payment</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Comprehensive Health Assessment (45-60 min)</td>
                  <td>$220.00</td>
                  <td>$148.20</td>
                  <td>$71.80</td>
                </tr>
                <tr>
                  <td>Pre-Employment Medical</td>
                  <td>$250.00</td>
                  <td>Not eligible</td>
                  <td>$250.00</td>
                </tr>
                <tr>
                  <td>Insurance Medical</td>
                  <td>$280.00</td>
                  <td>Not eligible</td>
                  <td>$280.00</td>
                </tr>
                <tr>
                  <td>Commercial Driver's License Medical</td>
                  <td>$180.00</td>
                  <td>Not eligible</td>
                  <td>$180.00</td>
                </tr>
                <tr>
                  <td>Travel Consultation & Vaccinations</td>
                  <td>$95.00 + vaccines</td>
                  <td>$39.75</td>
                  <td>$55.25 + vaccines</td>
                </tr>
                <tr>
                  <td>Mental Health Treatment Plan</td>
                  <td>$165.00</td>
                  <td>$113.30</td>
                  <td>$51.70</td>
                </tr>
                <tr>
                  <td>Chronic Disease Management Plan</td>
                  <td>$165.00</td>
                  <td>$113.30</td>
                  <td>$51.70</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Payment Information */}
      <section className="section payment-info">
        <div className="container">
          <div className="section-title">
            <h2>Payment Information</h2>
            <p>Payment methods and policies</p>
          </div>

          <div className="payment-methods">
            <div className="payment-method-card">
              <div className="method-icon">
                <FontAwesomeIcon icon={faCreditCard} />
              </div>
              <h3>Payment Methods</h3>
              <p>We accept EFTPOS, Visa, Mastercard, cash, and direct bank transfers. Payment is required at the time of consultation.</p>
            </div>

            <div className="payment-method-card">
              <div className="method-icon">
                <FontAwesomeIcon icon={faHandHoldingUsd} />
              </div>
              <h3>Medicare Claims</h3>
              <p>We offer on-the-spot Medicare claiming. Please ensure your bank details are registered with Medicare for immediate rebates.</p>
            </div>

            <div className="payment-method-card">
              <div className="method-icon">
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
              </div>
              <h3>Invoicing</h3>
              <p>For pre-approved WorkCover, TAC, or DVA patients, we can bill the relevant authority directly. Please provide all necessary documentation.</p>
            </div>

            <div className="payment-method-card">
              <div className="method-icon">
                <FontAwesomeIcon icon={faIdCard} />
              </div>
              <h3>Private Health</h3>
              <p>General practice services are typically covered by Medicare, not private health insurance. Some procedures may be eligible for private health rebates.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Fee Policy */}
      <section className="section fee-policy">
        <div className="container">
          <div className="policy-content">
            <h2>Fee Policy Information</h2>
            <div className="policy-grid">
              <div className="policy-item">
                <h3>Missed Appointments</h3>
                <p>A fee of $50 may be charged for appointments that are not attended without providing at least 24 hours' notice. This fee is not claimable from Medicare.</p>
              </div>
              <div className="policy-item">
                <h3>Late Cancellations</h3>
                <p>Cancellations with less than 24 hours' notice may incur a fee of $30, especially for repeated late cancellations. This fee is not claimable from Medicare.</p>
              </div>
              <div className="policy-item">
                <h3>Forms & Certificates</h3>
                <p>Completion of forms and certificates without a consultation may incur a fee ranging from $20 to $150 depending on complexity. These services are generally not covered by Medicare.</p>
              </div>
              <div className="policy-item">
                <h3>Scripts & Referrals</h3>
                <p>Repeat prescriptions or referrals without a consultation may incur a fee of $20. These services are not covered by Medicare and require doctor approval.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section fee-faq">
        <div className="container">
          <div className="section-title">
            <h2>Frequently Asked Questions</h2>
            <p>Common questions about our fees and billing</p>
          </div>

          <div className="faq-container">
            <div className="faq-item">
              <div className="faq-question">
                <FontAwesomeIcon icon={faQuestionCircle} className="question-icon" />
                <h3>Why do your fees exceed the Medicare rebate?</h3>
              </div>
              <div className="faq-answer">
                <p>Medicare rebates have not kept pace with the increasing costs of providing quality healthcare. The gap between our fees and the Medicare rebate reflects the true cost of providing comprehensive medical care, maintaining modern facilities, and employing skilled staff.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <FontAwesomeIcon icon={faQuestionCircle} className="question-icon" />
                <h3>How do I know if I'm eligible for bulk billing?</h3>
              </div>
              <div className="faq-answer">
                <p>Eligibility for bulk billing is determined by several factors including age, concession card status, and the nature of your medical condition. Please bring any relevant cards to your appointment, and our reception staff can advise you on your eligibility.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <FontAwesomeIcon icon={faQuestionCircle} className="question-icon" />
                <h3>How do I claim my Medicare rebate?</h3>
              </div>
              <div className="faq-answer">
                <p>We offer on-the-spot Medicare claiming at our practice. After paying for your consultation, we can process your Medicare rebate immediately if you have registered your bank details with Medicare. The rebate is typically deposited into your account within 24-48 hours.</p>
              </div>
            </div>

            <div className="faq-item">
              <div className="faq-question">
                <FontAwesomeIcon icon={faQuestionCircle} className="question-icon" />
                <h3>Why might the doctor charge more than the listed fee?</h3>
              </div>
              <div className="faq-answer">
                <p>The listed fees are for standard consultations and procedures. If your consultation is more complex or time-consuming than anticipated, or if additional procedures are required, the fee may be higher. Your doctor will inform you if additional costs apply.</p>
              </div>
            </div>
          </div>

          <div className="faq-more">
            <p>For more detailed information about our fees and billing policies, please visit our <Link to="/faq">FAQ page</Link> or contact our reception team.</p>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Have Questions About Our Fees?</h2>
            <p>Our friendly reception team is here to help with any questions about our fees and payment options.</p>
            <Link to="/contact" className="btn-primary">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Fees;
